import React from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Top-level error boundary that ships React render crashes into the backend
 * crash tracker (POST /api/crash/report) and shows a recoverable error screen.
 * Wrap <App /> with <ErrorBoundary>.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: '' };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: error?.message || 'Unknown error' };
  }

  componentDidCatch(error, info) {
    const msg = (error?.message || '') + ' ' + (error?.name || '');
    // Self-heal: stale HTML pointing at a deleted JS chunk (post-deploy cache miss).
    // Force a single hard reload with cache-bust to pick up the new bundle.
    const isStaleBundle = /ChunkLoadError|Loading chunk|Can't find variable|is not defined/i.test(msg);
    if (isStaleBundle) {
      try {
        const already = sessionStorage.getItem('blkmrkt_stale_reload_fired');
        if (!already) {
          sessionStorage.setItem('blkmrkt_stale_reload_fired', String(Date.now()));
          const u = new URL(window.location.href);
          u.searchParams.set('_v', String(Date.now()));
          window.location.replace(u.toString());
          return;
        }
      } catch { /* swallow */ }
    }
    try {
      axios.post(`${API}/crash/report`, {
        source: 'frontend',
        error_class: error?.name || 'Error',
        error_message: error?.message || '',
        stack: (info?.componentStack || error?.stack || '').slice(0, 8000),
        url: typeof window !== 'undefined' ? window.location.href : null,
        user_email: (() => {
          try {
            const t = localStorage.getItem('blkmrkt_token') || sessionStorage.getItem('blkmrkt_token');
            if (!t) return null;
            const payload = JSON.parse(atob(t.split('.')[1] || ''));
            return payload?.email || null;
          } catch { return null; }
        })(),
      }, { timeout: 5000 }).catch(() => { /* silent */ });
    } catch { /* silent */ }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-black flex items-center justify-center p-6">
          <div className="max-w-md w-full border border-red-500/40 bg-zinc-950 p-6 text-center">
            <p className="font-mono text-xs text-red-500 tracking-widest mb-3">SOMETHING WENT WRONG</p>
            <p className="font-mono text-[11px] text-zinc-500 mb-5 break-words">{this.state.message}</p>
            <p className="font-mono text-[10px] text-zinc-600 mb-5">
              The error has been logged. Try reloading — if it persists, it'll appear in /admin/crashes.
            </p>
            <button
              onClick={() => { try { window.location.reload(); } catch { /* */ } }}
              className="px-5 py-2 bg-tiffany-500 text-black font-mono text-xs tracking-widest hover:bg-tiffany-400"
              data-testid="error-boundary-reload"
            >
              RELOAD
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
